import AppForms from "@toothfairy/shared-ui/AppForms";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import Constants from "../../../Constants";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import useSections from "../../../Hooks/useSections";
import { isLargeScreen } from "../../MainViewContainer";
import ColorPickerField from "../../../Components/ToothFairyComponents/ColorPickerField";
import MermaidChartPreview from "./MermaidChartPreview";

const hexField = (t, name) =>
  AppForms.yup
    .string()
    .nullable()
    .test(
      "is-hex",
      t("admin.charting.validation.invalidHex"),
      (value) => !value || /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value)
    );

const hexFieldNames = [
  "primaryColor",
  "secondaryColor",
  "tertiaryColor",
  "primaryTextColor",
  "primaryBorderColor",
  "lineColor",
  "plotPrimaryColor",
  "plotSecondaryColor",
  "plotTertiaryColor",
  "xyBackgroundColor",
  "titleColor",
  "xAxisLabelColor",
  "yAxisLabelColor",
  "xAxisTitleColor",
  "yAxisTitleColor",
  "xAxisLineColor",
  "yAxisLineColor",
  "xAxisTickColor",
  "yAxisTickColor",
  "pie1",
  "pie2",
  "pie3",
  "pie4",
  "pie5",
  "quadrant1Fill",
  "quadrant2Fill",
  "quadrant3Fill",
  "quadrant4Fill",
  "quadrant1TextFill",
  "quadrant2TextFill",
  "quadrant3TextFill",
  "quadrant4TextFill",
  "quadrantPointFill",
];

const ChartingSettingsView = ({ onSubmit = () => {}, isSaveInProgress }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { getActiveWorkspace } = useWorkspaces();
  const { marginLeftUnderAppExpander } = useSections();
  const AppToasty = AppToast.useToast();
  const [formValues, setFormValues] = useState({});

  const chartingSchema = AppForms.yup.object().shape(
    Object.fromEntries(hexFieldNames.map((n) => [n, hexField(t, n)]))
  );

  const _chartingConfig = getActiveWorkspace()?.customChartingConfig
    ? JSON.parse(getActiveWorkspace()?.customChartingConfig)
    : {};

  return (
    <View
      style={{
        width: Platform.OS === "web" && isLargeScreen() ? 850 : 350,
        maxWidth: 850,
        marginTop: 20,
        marginLeft: marginLeftUnderAppExpander,
        alignSelf: "center",
        flexDirection: Platform.OS === "web" && isLargeScreen() ? "row" : "column",
        gap: 30,
        alignItems: "flex-start",
      }}
    >
      <View style={{ flex: 1, minWidth: 300, maxWidth: 500 }}>
        <AppForms.AppForm
          style={{
            minWidth: Platform.OS === "web" && isLargeScreen() ? 350 : 250,
            maxWidth: 350,
            alignSelf: "center",
          }}
          initialValues={{
            primaryColor: _chartingConfig?.primaryColor,
            secondaryColor: _chartingConfig?.secondaryColor,
            tertiaryColor: _chartingConfig?.tertiaryColor,
            primaryTextColor: _chartingConfig?.primaryTextColor,
            primaryBorderColor: _chartingConfig?.primaryBorderColor,
            lineColor: _chartingConfig?.lineColor,
            plotPrimaryColor: _chartingConfig?.plotPrimaryColor,
            plotSecondaryColor: _chartingConfig?.plotSecondaryColor,
            plotTertiaryColor: _chartingConfig?.plotTertiaryColor,
            xyBackgroundColor: _chartingConfig?.xyBackgroundColor,
            titleColor: _chartingConfig?.titleColor,
            xAxisLabelColor: _chartingConfig?.xAxisLabelColor,
            yAxisLabelColor: _chartingConfig?.yAxisLabelColor,
            xAxisTitleColor: _chartingConfig?.xAxisTitleColor,
            yAxisTitleColor: _chartingConfig?.yAxisTitleColor,
            xAxisLineColor: _chartingConfig?.xAxisLineColor,
            yAxisLineColor: _chartingConfig?.yAxisLineColor,
            xAxisTickColor: _chartingConfig?.xAxisTickColor,
            yAxisTickColor: _chartingConfig?.yAxisTickColor,
            pie1: _chartingConfig?.pie1,
            pie2: _chartingConfig?.pie2,
            pie3: _chartingConfig?.pie3,
            pie4: _chartingConfig?.pie4,
            pie5: _chartingConfig?.pie5,
            quadrant1Fill: _chartingConfig?.quadrant1Fill,
            quadrant2Fill: _chartingConfig?.quadrant2Fill,
            quadrant3Fill: _chartingConfig?.quadrant3Fill,
            quadrant4Fill: _chartingConfig?.quadrant4Fill,
            quadrant1TextFill: _chartingConfig?.quadrant1TextFill,
            quadrant2TextFill: _chartingConfig?.quadrant2TextFill,
            quadrant3TextFill: _chartingConfig?.quadrant3TextFill,
            quadrant4TextFill: _chartingConfig?.quadrant4TextFill,
            quadrantPointFill: _chartingConfig?.quadrantPointFill,
          }}
          enableReinitialize
          onSubmit={onSubmit}
          validationSchema={chartingSchema}
          onValuesChange={(values) => setFormValues(values)}
        >
          <AppExpander
            title={t("admin.charting.basicColorsTitle")}
            icon={AppAssets.icons.art}
            style={{ minWidth: isLargeScreen() ? 530 : 250 }}
            isExpandedOnLoad={true}
          >
            <View style={{ paddingLeft: 10 }}>
              {[
                "primaryColor",
                "secondaryColor",
                "tertiaryColor",
                "primaryTextColor",
                "primaryBorderColor",
                "lineColor",
              ].map((name) => (
                <ColorPickerField
                  key={name}
                  label={t(`admin.charting.${name}`)}
                  name={name}
                  placeholder={t("admin.charting.hexPlaceholder", {
                    color: t(`admin.charting.${name}`),
                  })}
                  previewTop={39}
                />
              ))}
            </View>
          </AppExpander>

          <AppExpander
            title={t("admin.charting.plotColorsTitle")}
            icon={AppAssets.icons.charting}
            style={{ minWidth: isLargeScreen() ? 530 : 250 }}
            isExpandedOnLoad={false}
          >
            <View style={{ paddingLeft: 10 }}>
              {[
                "plotPrimaryColor",
                "plotSecondaryColor",
                "plotTertiaryColor",
                "xyBackgroundColor",
                "titleColor",
                "xAxisLabelColor",
                "yAxisLabelColor",
                "xAxisTitleColor",
                "yAxisTitleColor",
                "xAxisLineColor",
                "yAxisLineColor",
                "xAxisTickColor",
                "yAxisTickColor",
              ].map((name) => (
                <ColorPickerField
                  key={name}
                  label={t(`admin.charting.${name}`)}
                  name={name}
                  placeholder={t("admin.charting.hexPlaceholder", {
                    color: t(`admin.charting.${name}`),
                  })}
                  previewTop={39}
                />
              ))}
            </View>
          </AppExpander>

          <AppExpander
            title={t("admin.charting.pieColorsTitle")}
            icon={AppAssets.icons.charting}
            style={{ minWidth: isLargeScreen() ? 530 : 250 }}
            isExpandedOnLoad={false}
          >
            <View style={{ paddingLeft: 10 }}>
              {["pie1", "pie2", "pie3", "pie4", "pie5"].map((name) => (
                <ColorPickerField
                  key={name}
                  label={t(`admin.charting.${name}`)}
                  name={name}
                  placeholder={t("admin.charting.hexPlaceholder", {
                    color: t(`admin.charting.${name}`),
                  })}
                  previewTop={39}
                />
              ))}
            </View>
          </AppExpander>

          <AppExpander
            title={t("admin.charting.quadrantColorsTitle")}
            icon={AppAssets.icons.charting}
            style={{ minWidth: isLargeScreen() ? 530 : 250 }}
            isExpandedOnLoad={false}
          >
            <View style={{ paddingLeft: 10 }}>
              {[
                "quadrant1Fill",
                "quadrant2Fill",
                "quadrant3Fill",
                "quadrant4Fill",
                "quadrant1TextFill",
                "quadrant2TextFill",
                "quadrant3TextFill",
                "quadrant4TextFill",
                "quadrantPointFill",
              ].map((name) => (
                <ColorPickerField
                  key={name}
                  label={t(`admin.charting.${name}`)}
                  name={name}
                  placeholder={t("admin.charting.hexPlaceholder", {
                    color: t(`admin.charting.${name}`),
                  })}
                  previewTop={39}
                />
              ))}
            </View>
          </AppExpander>

          <AppForms.AppSubmitButton
            title={t("admin.charting.saveButton")}
            isDisabled={isSaveInProgress}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,
                marginTop: 20,
                alignSelf: "center",
                height: Constants.appButtonHeight,
              },
            ]}
          />
        </AppForms.AppForm>
      </View>

      {Platform.OS === "web" && (
        <MermaidChartPreview values={formValues} />
      )}
    </View>
  );
};

export default ChartingSettingsView;
